import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { MdAdd, MdDelete, MdInfoOutline } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'

import type { Props as BuilderFormProps } from './BuilderFormSection'
import type { ChangeEventHandler, MouseEvent, ReactElement } from 'react'
import type { IBuilderConfig, IBuilderConfigSeries, IChartConfigApiModel, Unit } from '~/types/graph-builder'

import BuilderFormSection from './BuilderFormSection'
import SeriesFields, { defaultConfig as seriesDefaultConfig } from './SeriesFields'
import YAxisFields from './YAxisFields'

import Button from '~/components/Buttons/Button'
import MultiSelectInput from '~/components/Inputs/ReactHookForm/MultiSelectInput'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import TextArea from '~/components/Inputs/ReactHookForm/TextArea'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import ModalDialog from '~/components/ModalDialog'
import Tooltip from '~/components/Tooltip'
import { AUTHORIZATIONS, DEFAULT_DAI_MULTIPLE_VALUES, FORM_ERRORS } from '~/const'
import { analyticsColors } from '~/const/chart'
import { GRAPH_TYPE, breakdownOptions } from '~/const/graphs-builder'
import useApps from '~/hooks/useApps'
import { useAnalyticsContext } from '~/providers/Analytics'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useSearchParamsContext } from '~/providers/SearchParams'
import { useGlossaryListQuery } from '~/queries/glossary'
import {
  keys as graphConfigsKeys,
  useCreateChartConfigMutation,
  useDeleteChartConfigMutation,
  useUpdateChartConfigMutation,
} from '~/queries/graphs-builder'
import {
  featureNames,
  Mode,
  CA_PAGES,
  CA_SECTIONS,
  BUSINESS_REPORTS_PAGES,
  BUSINESS_REPORTS_SECTIONS,
} from '~/types/graph-builder'
import { isBusinessReportsFeature } from '~/utils/analytics-context'
import { appKeyDeviceTypeToString } from '~/utils/application'
import { repeatItemInArray } from '~/utils/array'
import { displayToast } from '~/utils/toast'
import { businessReportsDefaultFilterDisplay, caDefaultFilterDisplay } from '~/views/Analytics/Router'

import './styles.scss'

breakdownOptions.unshift({ label: 'None', value: '' })

const featureNameOptions = featureNames.map((el) => ({ label: el, value: el }))

const forceKpiFieldOptions = [
  { label: 'No', value: '' },
  { label: 'Installation Date', value: 'installation_date' },
  { label: 'Day After Install', value: 'dai' },
]

const seriesFieldsHelper: BuilderFormProps[] = [
  { primaryColor: analyticsColors[0], secondaryColor: `${analyticsColors[0]}1A`, title: 'Series 1' },
  { primaryColor: analyticsColors[1], secondaryColor: `${analyticsColors[1]}1A`, title: 'Series 2' },
  { primaryColor: analyticsColors[2], secondaryColor: `${analyticsColors[2]}1A`, title: 'Series 3' },
  { primaryColor: analyticsColors[3], secondaryColor: `${analyticsColors[3]}1A`, title: 'Series 4' },
]

const yAxisFieldsHelper: BuilderFormProps[] = [
  { primaryColor: '#CA3423', secondaryColor: '#CA34231A', title: 'Y-Axis 1' },
  { primaryColor: '#06703D', secondaryColor: '#06703D1A', title: 'Y-Axis 2' },
]

const yAxesDefaultConfig = [
  {
    name: '1',
    precision: 2,
    unit: 'percent' as Unit,
  },
  {
    name: '2',
    precision: 2,
    unit: 'percent' as Unit,
  },
]

const businessReportsYAxesDefaultConfig = [
  {
    name: '1',
    precision: 2,
    unit: 'raw' as Unit,
  },
  {
    name: '2',
    precision: 2,
    unit: 'raw' as Unit,
  },
]

const SERIES_COUNT_MAX = 4
const Y_AXES_COUNT_MAX = 2

export const getDefaultConfig = (isBusinessReport?: boolean): IBuilderConfig => ({
  applications: [],
  column: 0,
  featureName: 'analytics_aggregated',
  line: 0,
  mode: Mode.NORMAL,
  nbLastDaysToIgnore: 0,
  page: undefined,
  position: 0,
  requiredBreakdown: '',
  section: undefined,
  series: repeatItemInArray({
    count: SERIES_COUNT_MAX,
    item: seriesDefaultConfig,
  }) as IBuilderConfigSeries[],
  title: 'builder graph',
  tooltip: {
    how: '',
    relatedGlossaryItems: [],
    what: '',
  },
  wantNegativeEvolution: false,
  yAxes: isBusinessReport ? businessReportsYAxesDefaultConfig : yAxesDefaultConfig,
})

const validationSchema = Yup.object().shape({
  applications: Yup.array().of(Yup.string().trim().required()).required(),
  column: Yup.number()
    .typeError(FORM_ERRORS.NUMBER)
    .when('series.0.graphType', {
      is: (value: string) => value === GRAPH_TYPE.RAW,
      otherwise: (schema) => schema,
      then: (schema) => schema.required().min(0, FORM_ERRORS.MIN_VALUE(0)),
    }),
  featureName: Yup.string().trim(),
  forceKPIField: Yup.string().trim().oneOf(['', 'installation_date', 'dai']),
  line: Yup.number()
    .typeError(FORM_ERRORS.NUMBER)
    .when('series.0.graphType', {
      is: (value: string) => value === GRAPH_TYPE.RAW,
      otherwise: (schema) => schema,
      then: (schema) => schema.required().min(0, FORM_ERRORS.MIN_VALUE(0)),
    }),
  mode: Yup.string().trim().oneOf(Object.values(Mode)).required(),
  nbLastDaysToIgnore: Yup.number().typeError(FORM_ERRORS.NUMBER).required().min(0, FORM_ERRORS.MIN_VALUE(0)),
  page: Yup.string().trim(),
  position: Yup.number()
    .typeError(FORM_ERRORS.NUMBER)
    .when('series.0.graphType', {
      is: (value: string) => value === GRAPH_TYPE.RAW,
      otherwise: (schema) => schema.required().min(0, FORM_ERRORS.MIN_VALUE(0)),
      then: (schema) => schema,
    }),
  requiredBreakdown: Yup.string().trim(),
  section: Yup.string().trim(),
  series: Yup.array()
    .of(
      Yup.object({
        defaultBreakdown: Yup.string().trim(),
        graphType: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
        minNbu: Yup.number()
          .typeError(FORM_ERRORS.NUMBER)
          .test(
            'isSeries0MinNbuWhenIsGraphTypeNotRaw',
            FORM_ERRORS.REQUIRED,
            function (this: Yup.TestContext, fieldValue: any): boolean {
              return this.parent.graphType === GRAPH_TYPE.RAW || fieldValue !== undefined
            }
          ),
        normalizedBy: Yup.array().of(Yup.string().trim().required()).required(),
        values: Yup.array().of(Yup.string().trim().required()).required().min(1, FORM_ERRORS.AT_LEAST_ONE),
        withInf: Yup.boolean(),
        withSup: Yup.boolean(),
        withTrend: Yup.boolean(),
        yAxisName: Yup.string()
          .trim()
          .test(
            'isSeries0YAxisNameWhenIsGraphTypeNotRaw',
            FORM_ERRORS.REQUIRED,
            function (this: Yup.TestContext, fieldValue: any): boolean {
              return this.parent.graphType === GRAPH_TYPE.RAW || fieldValue !== undefined
            }
          ),
      })
    )
    .required(),
  title: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  tooltip: Yup.object({
    how: Yup.string().trim(),
    relatedGlossaryItems: Yup.array().of(Yup.string().trim().required()).required(),
    what: Yup.string().trim(),
  }),
  wantNegativeEvolution: Yup.boolean(),
  warningMessage: Yup.string().trim(),
  yAxes: Yup.array()
    .of(
      Yup.object({
        name: Yup.string().test(
          'isYAxis0NameWhenIsGraphTypeNotRaw',
          FORM_ERRORS.REQUIRED,
          function (this: Yup.TestContext, fieldValue?: string): boolean {
            return this.from?.[1].value.series[0].graphType === GRAPH_TYPE.RAW || fieldValue !== undefined
          }
        ),
        precision: Yup.number().typeError(FORM_ERRORS.NUMBER).required().min(0, FORM_ERRORS.MIN_VALUE(0)),
        unit: Yup.string().oneOf(['duration_in_seconds', 'euro', 'percent', 'raw']).required(),
      })
    )
    .required(),
})

interface Props {
  config?: IChartConfigApiModel
  isConfigLoading?: boolean
  onConfigChange: (newConfig: IBuilderConfig) => void
}

const BuilderForm = ({ config, isConfigLoading, onConfigChange }: Props): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const navigate = useNavigate()

  const { dispatch, state } = useAnalyticsContext()

  const { dispatchToSearchParams } = useSearchParamsContext()

  const [seriesFieldsCount, setSeriesFieldsCount] = useState(config?.series.length || 1)

  const [yAxisFieldsCount, setYAxisFieldsCount] = useState(config?.y_axes.length || 1)

  const defaultConfig = useMemo(() => getDefaultConfig(state.feature.includes('business_report')), [state.feature])

  const existingConfig = useMemo(
    () =>
      config
        ? {
            applications: config.applications.map(({ app_key, device_type }) => `${app_key}:${device_type}`),
            column: config.column,
            featureName: config.feature_name,
            forceKPIField: config.force_kpi_field,
            line: config.line,
            mode: config.mode,
            nbLastDaysToIgnore: config.nb_last_days_to_ignore,
            page: config.page as CA_PAGES,
            position: config.position,
            requiredBreakdown: config.required_breakdown,
            section: config.section,
            series: [
              ...config.series.map((el) => ({
                defaultBreakdown: el.default_breakdown,
                graphType: el.graph_type,
                minNbu: el.min_nbu,
                normalizedBy: el.normalized_by,
                values: el.values,
                withInf: el.with_inf,
                withSup: el.with_sup,
                withTrend: el.with_trend,
                yAxisName: el.y_axes_name,
              })),
              ...(repeatItemInArray({
                count: SERIES_COUNT_MAX - seriesFieldsCount,
                item: seriesDefaultConfig,
              }) as IBuilderConfigSeries[]),
            ],
            title: config.title,
            tooltip: {
              how: config.how || '',
              relatedGlossaryItems: config.related_glossary_items || [],
              what: config.what || '',
            },
            wantNegativeEvolution: config.want_negative_evolution,
            yAxes:
              config.y_axes.length === 2
                ? config.y_axes
                : config.y_axes.length === 1
                  ? [config.y_axes[0], yAxesDefaultConfig[1]]
                  : yAxesDefaultConfig,
          }
        : undefined,
    [config, seriesFieldsCount]
  )

  const methods = useForm<IBuilderConfig>({
    defaultValues: existingConfig || defaultConfig,
    resolver: yupResolver<Omit<IBuilderConfig, 'id'>>(validationSchema),
    shouldUnregister: true,
  })

  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = methods

  const [sectionsOptions, setSectionsOptions] = useState<IOption[]>([])

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const watchAllFields = watch()

  const watchFeature = watch('featureName')

  const watchTooltipHow = watch('tooltip.how')

  const watchTooltipWhat = watch('tooltip.what')

  const watchPage = watch('page')

  const watchSeries0GraphType = watch('series.0.graphType')

  const isBusinessReports = isBusinessReportsFeature(watchFeature)

  const isUserAuthorizedToManageConfigs = useIsUserAuthorized([AUTHORIZATIONS.ANALYTICS.GRAPH_BUILDER.WRITE])

  const pagesOptions = Object.values(isBusinessReports ? BUSINESS_REPORTS_PAGES : CA_PAGES)
    .filter(([v]) => isNaN(Number(v)))
    .map((value) => ({ label: value, value: value }))

  /** Queries & Mutations */

  const { data: glossaryItems, isLoading: isGlossaryItemsLoading } = useGlossaryListQuery()

  const { mutateAsync: createChartConfig } = useCreateChartConfigMutation()

  const { mutateAsync: updateChartConfig } = useUpdateChartConfigMutation()

  const { mutateAsync: deleteConfig } = useDeleteChartConfigMutation()

  const glossaryOptions =
    glossaryItems?.map(({ title }) => ({
      label: title,
      value: title,
    })) || []

  const { appsOptions, isAppsLoading } = useApps()

  const formattedAppsOptions = appsOptions
    .map((el) => ({
      ...el,
      label: `${el.appKey} - ${el.deviceType}`,
      value: appKeyDeviceTypeToString(el.appKey, el.deviceType),
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  /** Effects */

  useEffect(() => {
    onConfigChange(watchAllFields)
  }, [onConfigChange, watchAllFields])

  useEffect(() => {
    if (watchPage && !isConfigLoading) {
      const options = Object.entries(
        isBusinessReports
          ? BUSINESS_REPORTS_SECTIONS[watchPage as BUSINESS_REPORTS_PAGES]
          : CA_SECTIONS[watchPage as CA_PAGES]
      ).map(([, value]) => ({
        label: value,
        value: value,
      }))
      setSectionsOptions(options)
    }
  }, [isBusinessReports, isConfigLoading, watchPage])

  useEffect(() => {
    dispatch({
      payload: isBusinessReports ? businessReportsDefaultFilterDisplay : caDefaultFilterDisplay,
      type: 'SET_DISPLAYED_FILTERS',
    })
  }, [dispatch, isBusinessReports])

  useEffect(() => {
    if (config?.y_axes) setYAxisFieldsCount(config.y_axes.length)
  }, [config?.y_axes])

  useEffect(() => {
    if (config?.series) setSeriesFieldsCount(config.series.length)
  }, [config?.series])

  /** Handlers */

  const setPrecisionValueAccordingToUnit = ({ unit, unitFieldName }: { unit: string; unitFieldName: string }) => {
    const precisionFieldName = unitFieldName === 'yAxes.0.unit' ? 'yAxes.0.precision' : 'yAxes.1.precision'

    switch (unit) {
      case 'euro':
        setValue(precisionFieldName as keyof IBuilderConfig, 3)
        break
      case 'duration_in_seconds':
        setValue(precisionFieldName as keyof IBuilderConfig, 0)
        break
      default:
        setValue(precisionFieldName as keyof IBuilderConfig, 2)
    }
  }

  const setFilters = useCallback(
    (filters: Record<string, string[]>) => {
      dispatch({ payload: filters, type: 'SET_FILTERS' })
      dispatchToSearchParams({ type: 'update', value: filters })
    },
    [dispatch, dispatchToSearchParams]
  )

  const handleFormSubmit = (submitType: 'edit' | 'save' | 'save&create') => async (values: IBuilderConfig) => {
    if (submitType === 'edit' && config) {
      // Update an existing config
      try {
        await updateChartConfig({
          ...values,
          configId: config.graph_config_id,
          position: values.position || 0,
          series: values.series.map((el) => ({
            ...el,
            minNbu: el.minNbu || 100,
            withInf: el.withInf || false,
            withSup: el.withSup || false,
            withTrend: el.withTrend || false,
          })),
          wantNegativeEvolution: values.wantNegativeEvolution || false,
        })
        await queryClient.invalidateQueries({
          queryKey: [...graphConfigsKeys.all, graphConfigsKeys.getOne(config.graph_config_id)],
        })
        displayToast('Chart config successfully edited', 'success')
      } catch {
        displayToast('Unable to edit the chart config', 'error')
      }
    } else {
      // Create a new config
      try {
        const isCreatingFromEditView = config && submitType === 'save&create'

        const newConfig = await createChartConfig({
          ...values,
          position: values.position || 0,
          series: values.series.map((el) => ({
            ...el,
            minNbu: el.minNbu || 100,
            withInf: el.withInf || false,
            withSup: el.withSup || false,
            withTrend: el.withTrend || false,
          })),
          wantNegativeEvolution: values.wantNegativeEvolution || false,
        })

        await queryClient.invalidateQueries({ queryKey: graphConfigsKeys.all })
        displayToast('Chart config successfully created', 'success')

        if (submitType === 'save' || isCreatingFromEditView) {
          navigate(`/analytics/ca/builder/${newConfig.graph_config_id}`, { replace: isCreatingFromEditView })
        }

        reset({ ...defaultConfig, applications: values.applications })
      } catch {
        displayToast('Unable to create the chart config', 'error')
      }
    }
  }

  const handleSelectFieldChange = (field: keyof IBuilderConfig, selectedOption: IOption | IOption[] | null) => {
    setValue(
      field,
      Array.isArray(selectedOption)
        ? selectedOption.map(({ value }) => value)
        : selectedOption
          ? selectedOption.value
          : undefined,
      {
        shouldDirty: true,
        shouldTouch: true,
      }
    )

    if (field.startsWith('series.') && field.endsWith('.defaultBreakdown')) {
      setFilters(
        !Array.isArray(selectedOption) && selectedOption?.value === 'dai'
          ? {
              breakdown: ['dai'],
              dai: DEFAULT_DAI_MULTIPLE_VALUES,
              kpi: [state.filters.kpi?.[0] ?? 'installation_date'],
            }
          : {
              breakdown: [],
              dai: [],
            }
      )
    }

    if (
      (field === ('yAxes.0.unit' as keyof IBuilderConfig) || field === ('yAxes.1.unit' as keyof IBuilderConfig)) &&
      !Array.isArray(selectedOption)
    ) {
      setPrecisionValueAccordingToUnit({
        unit: selectedOption?.value as keyof IBuilderConfig,
        unitFieldName: field,
      })
    }

    if (field === 'page') {
      setValue('section', undefined)
    }

    if (field === 'featureName' && selectedOption) {
      setValue(
        'yAxes',
        watchAllFields.yAxes.map((_, index) => ({
          name: `${index + 1}`,
          precision: 2,
          unit: 'raw' as Unit,
        }))
      )

      dispatch({
        payload: Array.isArray(selectedOption) ? '' : selectedOption?.value,
        type: 'SET_FEATURE',
      })
      dispatch({
        payload: Array.isArray(selectedOption)
          ? ''
          : selectedOption?.value.includes('business_reports')
            ? businessReportsDefaultFilterDisplay
            : caDefaultFilterDisplay,
        type: 'SET_DISPLAYED_FILTERS',
      })
    }
  }

  const handleFieldChange =
    (field: string): ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> =>
    (evt) => {
      // @ts-expect-error how to??
      setValue(field, evt.currentTarget.value, {
        shouldDirty: true,
        shouldTouch: true,
      })
    }

  const handleDeleteModalState = (isOpen: boolean) => () => setIsDeleteModalOpen(isOpen)

  const handleDeleteConfigurationConfirm = async () => {
    if (config) {
      try {
        await deleteConfig({ configId: config.graph_config_id })
        await queryClient.invalidateQueries({ queryKey: graphConfigsKeys.all })
        setIsDeleteModalOpen(false)
        displayToast('Configuration successfully deleted', 'success')
        navigate('/chart-configs')
      } catch {
        displayToast('Configuration could not be deleted', 'error')
      }
    }
  }

  const handleAddSeriesButton = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (seriesFieldsCount < 4) setSeriesFieldsCount((prev) => prev + 1)
  }

  const handleAddYAxisButtonClick = () => {
    if (yAxisFieldsCount < 2) setYAxisFieldsCount((prev) => prev + 1)
  }

  const handleDeleteSeriesButtonClick = (seriesIndex: number) => {
    const seriesFormState = [...getValues('series')]
    seriesFormState.splice(seriesIndex, 1)
    setSeriesFieldsCount((prev) => prev - 1)
    setValue('series', seriesFormState)
  }

  const handleDeleteYAxisButtonClick = (yAxisIndex: number) => {
    const yAxesFormState = [...getValues('yAxes')]
    yAxesFormState.splice(yAxisIndex, 1)
    setYAxisFieldsCount((prev) => prev - 1)
    setValue('yAxes', yAxesFormState)
  }

  const handleGraphTypeOptionClick = (graphType: GRAPH_TYPE) => {
    if ((graphType === GRAPH_TYPE.RAW || graphType === GRAPH_TYPE.STACKED_COLUMN_PERCENT) && yAxisFieldsCount > 1) {
      handleDeleteYAxisButtonClick(1)
    }
  }

  const handleFeatureChange = (value: unknown) => {
    const castedValue = value as IOption

    const isBusinessReports = isBusinessReportsFeature(castedValue.value)

    handleSelectFieldChange('featureName', castedValue)
    watchAllFields.series.map((_, index) => {
      setValue(`series.${index}.values`, isBusinessReports ? [] : ['iap_pred_360'])
      setValue(`series.${index}.normalizedBy`, isBusinessReports ? [] : ['nbu'])
    })
    handleSelectFieldChange('page', null)
  }

  /** Render */

  return (
    <>
      <FormProvider {...methods}>
        <form className="builder-form-component">
          <BuilderFormSection primaryColor="#00759A" secondaryColor="#00759A1A" title="Data Source">
            <Controller
              control={control}
              name="featureName"
              render={({ field }) => (
                <SelectInput
                  {...field}
                  isClearable={false}
                  label="Feature"
                  onChange={handleFeatureChange}
                  options={featureNameOptions}
                  placeholder="Select a feature"
                  value={featureNameOptions.find(({ value }) => value === field.value)}
                />
              )}
            />
          </BuilderFormSection>
          {seriesFieldsHelper.slice(0, seriesFieldsCount).map(({ primaryColor, secondaryColor, title }, idx) => (
            <SeriesFields
              handleDeletion={handleDeleteSeriesButtonClick}
              handleGraphTypeOptionClick={handleGraphTypeOptionClick}
              handleSelectFieldChange={handleSelectFieldChange}
              key={idx}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              seriesFieldsCount={seriesFieldsCount}
              seriesIndex={idx}
              title={title}
              yAxisFieldsCount={yAxisFieldsCount}
            />
          ))}
          {seriesFieldsCount < SERIES_COUNT_MAX &&
          watchSeries0GraphType !== GRAPH_TYPE.RAW &&
          watchSeries0GraphType !== GRAPH_TYPE.STACKED_COLUMN_PERCENT ? (
            <Button
              className="add-series-button"
              onClick={(e) => handleAddSeriesButton(e)}
              outlined
              role="button"
              style={{
                borderColor: seriesFieldsHelper[seriesFieldsCount].primaryColor,
                color: seriesFieldsHelper[seriesFieldsCount].primaryColor,
              }}
            >
              <MdAdd color={seriesFieldsHelper[seriesFieldsCount].primaryColor} size={16} />
              Add Series
            </Button>
          ) : null}
          {yAxisFieldsHelper.slice(0, yAxisFieldsCount).map(({ primaryColor, secondaryColor, title }, idx) => (
            <YAxisFields
              axisIndex={idx}
              key={idx}
              onDelete={handleDeleteYAxisButtonClick}
              onSelectFieldChange={handleSelectFieldChange}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              title={watchSeries0GraphType !== GRAPH_TYPE.RAW ? title : 'Unit and precision'}
            />
          ))}
          {yAxisFieldsCount < Y_AXES_COUNT_MAX &&
          watchSeries0GraphType !== GRAPH_TYPE.RAW &&
          watchSeries0GraphType !== GRAPH_TYPE.STACKED_COLUMN_PERCENT ? (
            <Button
              className="add-y-axis-button"
              onClick={handleAddYAxisButtonClick}
              outlined
              role="button"
              style={{
                borderColor: yAxisFieldsHelper[yAxisFieldsCount].primaryColor,
                color: yAxisFieldsHelper[yAxisFieldsCount].primaryColor,
              }}
            >
              <MdAdd color={yAxisFieldsHelper[yAxisFieldsCount].primaryColor} size={16} />
              Add Y-Axis
            </Button>
          ) : null}
          <BuilderFormSection primaryColor="#00759A" secondaryColor="#00759A1A" title="General Settings">
            <TextInput
              error={errors.nbLastDaysToIgnore?.message}
              label="Nb last days to exclude"
              {...register('nbLastDaysToIgnore', { valueAsNumber: true })}
              type="number"
            />
            {watchSeries0GraphType !== GRAPH_TYPE.RAW ? (
              <Controller
                control={control}
                name="requiredBreakdown"
                render={({ field }) => (
                  <SelectInput
                    {...field}
                    label="Required breakdown"
                    onChange={(value: unknown) => handleSelectFieldChange('requiredBreakdown', value as IOption)}
                    options={breakdownOptions}
                    placeholder="Select a required breakdown"
                    value={breakdownOptions.find(({ value }) => value === field.value)}
                  />
                )}
              />
            ) : null}
            {watchSeries0GraphType !== GRAPH_TYPE.RAW && !isBusinessReportsFeature ? (
              <Controller
                control={control}
                name="requiredBreakdown"
                render={({ field }) => (
                  <SelectInput
                    {...field}
                    label="Force KPI Field"
                    onChange={(value: unknown) => handleSelectFieldChange('forceKPIField', value as IOption)}
                    options={forceKpiFieldOptions}
                    placeholder="Select a page"
                    value={forceKpiFieldOptions.find(({ value }) => value === field.value)}
                  />
                )}
              />
            ) : null}
          </BuilderFormSection>
          <BuilderFormSection primaryColor="#00B2CA" secondaryColor="#00B2CA1A" title="General Display">
            <div className="title-container">
              <Tooltip
                mouseEnterDelay={0.2}
                overlay={<div>{`Use { } brackets to specify the colored part of title`}</div>}
                placement="top"
                trigger="hover"
              >
                <div className="tooltip-trigger-icon">
                  <MdInfoOutline color="#aeaeae" size={15} />
                </div>
              </Tooltip>
              <TextInput error={errors.title?.message} label="Title" required {...register('title')} />
            </div>
            <TextArea
              label="What"
              {...register('tooltip.what')}
              onChange={handleFieldChange('tooltip.what')}
              value={watchTooltipWhat || ''}
            />
            <TextArea
              label="How"
              {...register('tooltip.how')}
              onChange={handleFieldChange('tooltip.how')}
              value={watchTooltipHow || ''}
            />
            <Controller
              control={control}
              name="tooltip.relatedGlossaryItems"
              render={() => (
                <MultiSelectInput
                  isLoading={isGlossaryItemsLoading}
                  label="Related glossary entry"
                  onChange={(value: unknown) =>
                    handleSelectFieldChange('tooltip.relatedGlossaryItems' as keyof IBuilderConfig, value as IOption[])
                  }
                  options={glossaryOptions}
                  placeholder="Select a glossary entry"
                  value={glossaryOptions.filter((option) =>
                    getValues('tooltip.relatedGlossaryItems').includes(option.value)
                  )}
                />
              )}
            />
            <Controller
              control={control}
              name="page"
              render={({ field }) => (
                <SelectInput
                  {...field}
                  label="Page"
                  onChange={(value: unknown) => handleSelectFieldChange('page', value as IOption)}
                  options={pagesOptions}
                  placeholder="Select a page"
                  value={pagesOptions.find(({ value }) => value === field.value)}
                />
              )}
            />
            <Controller
              control={control}
              name="section"
              render={({ field }) => (
                <SelectInput
                  {...field}
                  isClearable
                  isDisabled={!watchPage || !sectionsOptions.length}
                  label="Section"
                  onChange={(value: unknown) => handleSelectFieldChange('section', value as IOption)}
                  options={sectionsOptions}
                  placeholder="Select a section in the page"
                  value={sectionsOptions.find(({ value }) => value === field.value)}
                />
              )}
            />
            {watchSeries0GraphType !== GRAPH_TYPE.RAW ? (
              <TextInput
                {...register('position', { valueAsNumber: true })}
                error={errors.position?.message}
                label="Position"
                type="number"
              />
            ) : (
              <>
                <TextInput
                  {...register('line', { valueAsNumber: true })}
                  error={errors.line?.message}
                  label="Line"
                  type="number"
                />
                <TextInput
                  {...register('column', { valueAsNumber: true })}
                  error={errors.column?.message}
                  label="Column"
                  type="number"
                />
              </>
            )}
            <Controller
              control={control}
              name="applications"
              render={() => (
                <MultiSelectInput
                  isLoading={isAppsLoading}
                  label="Show for these apps"
                  menuPlacement="top"
                  onChange={(value: unknown) => handleSelectFieldChange('applications', value as IOption[])}
                  options={formattedAppsOptions}
                  placeholder="Select applications"
                  value={formattedAppsOptions.filter(({ value }) => getValues('applications').includes(value))}
                />
              )}
            />
          </BuilderFormSection>
          {config ? (
            <div className="edit-actions-container">
              <Button className="submit-button" onClick={handleSubmit(handleFormSubmit('edit'))} outlined>
                Edit
              </Button>
              <Button
                className="submit-button save-and-create-button"
                onClick={handleSubmit(handleFormSubmit('save&create'))}
              >
                Save as new
              </Button>
              {isUserAuthorizedToManageConfigs ? (
                <Button className="delete-button" onClick={handleDeleteModalState(true)}>
                  <MdDelete color="#fff" />
                </Button>
              ) : null}
            </div>
          ) : (
            <div className="save-buttons-container">
              <Button className="submit-button" onClick={handleSubmit(handleFormSubmit('save'))} outlined>
                Save
              </Button>
              <Button
                className="submit-button save-and-create-button"
                onClick={handleSubmit(handleFormSubmit('save&create'))}
              >
                Save & create a new
              </Button>
            </div>
          )}
          <input {...register('mode')} type="hidden" value={Mode.NORMAL} />
        </form>
        <DevTool control={control} />
      </FormProvider>
      <ModalDialog
        isOpen={isDeleteModalOpen}
        mainText="Do you really want to delete this configuration?"
        onClose={handleDeleteModalState(false)}
        onConfirm={handleDeleteConfigurationConfirm}
      />
    </>
  )
}

export default memo(BuilderForm)
